/**
 * WebSocket autenticado (doc 08). Consome `wsServerEvent` de `@zapir/shared`,
 * valida cada frame e atualiza a UI via callbacks. Reconexao com backoff e
 * resync ao voltar (RF-UX-008/053; EC-UX-021 dedupe por message_id no cache
 * do TanStack Query, nao aqui).
 */
import { wsServerEvent, type WsServerEvent } from "@zapir/shared";
import { api } from "./api";

export type WsStatus = "connecting" | "open" | "reconnecting" | "closed";

export interface WsOptions {
  onEvent: (event: WsServerEvent) => void;
  onStatus: (status: WsStatus) => void;
  /** Chamado a cada reconexao: invalida/refaz o resync (messages?since=). */
  onResync?: () => void;
}

export interface WsHandle {
  close: () => void;
}

const HEARTBEAT_MS = 25_000;

function defaultUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/ws`;
}

export function connectRealtime(options: WsOptions): WsHandle {
  let socket: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let heartbeat: number | undefined;
  let reconnectTimer: number | undefined;
  let firstConnect = true;
  let generation = 0;

  const setStatus = (status: WsStatus) => options.onStatus(status);

  const clearTimers = () => {
    if (heartbeat) window.clearInterval(heartbeat);
    if (reconnectTimer) window.clearTimeout(reconnectTimer);
    heartbeat = undefined;
    reconnectTimer = undefined;
  };

  /**
   * Tentativas seguidas ate assumir que a conexao caiu de fato. Abaixo disso o
   * estado e "reconnecting" (transitorio, sem alarmar); a partir dai vira
   * "closed" -> a UI mostra "sem conexao" com honestidade em vez de ficar
   * "reconectando..." para sempre.
   */
  const FAILED_ATTEMPTS_UNTIL_OFFLINE = 4;

  const scheduleReconnect = () => {
    if (closed) return;
    attempt += 1;
    const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5));
    setStatus(attempt >= FAILED_ATTEMPTS_UNTIL_OFFLINE ? "closed" : "reconnecting");
    reconnectTimer = window.setTimeout(open, delay);
  };

  const open = async () => {
    if (closed) return;
    clearTimers();
    // Cada tentativa recebe um numero. `open` e assincrono (busca o ticket), e
    // `visibilitychange` no celular dispara varias vezes seguidas: sem isso duas
    // chamadas concorrentes abriam dois sockets, o orfao continuava agendando
    // reconexao e virava uma tempestade de conexoes (3 tickets no mesmo segundo,
    // visto no log de producao em 2026-09-19).
    const gen = ++generation;
    setStatus(firstConnect ? "connecting" : "reconnecting");
    let url = defaultUrl();
    try {
      const ticket = await api.realtimeTicket();
      const base = ticket.url ?? url;
      url = `${base}${base.includes("?") ? "&" : "?"}ticket=${encodeURIComponent(ticket.ticket)}`;
    } catch {
      // Sem ticket (API ausente/offline): tenta mesmo assim; falha -> reconecta.
    }
    if (closed || gen !== generation) return; // outra tentativa assumiu enquanto buscava o ticket

    let s: WebSocket;
    try {
      s = new WebSocket(url);
    } catch {
      scheduleReconnect();
      return;
    }
    socket = s;

    s.onopen = () => {
      if (gen !== generation) return void s.close();
      attempt = 0;
      setStatus("open");
      if (!firstConnect) options.onResync?.();
      firstConnect = false;
      heartbeat = window.setInterval(() => {
        if (s.readyState === WebSocket.OPEN) {
          s.send(JSON.stringify({ type: "ping" }));
        }
      }, HEARTBEAT_MS);
    };

    s.onmessage = (event) => {
      if (gen !== generation) return;
      if (typeof event.data !== "string") return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }
      const result = wsServerEvent.safeParse(parsed);
      if (!result.success) return;
      if (result.data.type === "pong") return;
      options.onEvent(result.data);
    };

    s.onerror = () => {
      /* onclose cuida da reconexao */
    };

    s.onclose = () => {
      if (gen !== generation) return; // socket superado: nao reagenda nada
      socket = null;
      clearTimers();
      scheduleReconnect();
    };
  };

  const onVisible = () => {
    if (document.visibilityState !== "visible") return;
    // CONNECTING conta como "ja tem tentativa em andamento": reabrir aqui era o
    // que multiplicava sockets ao acordar a tela.
    const busy =
      socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING;
    if (busy) return;
    attempt = 0;
    void open();
  };
  document.addEventListener("visibilitychange", onVisible);

  void open();

  return {
    close: () => {
      closed = true;
      generation += 1; // invalida qualquer tentativa em voo
      clearTimers();
      document.removeEventListener("visibilitychange", onVisible);
      // NAO reporta "closed" aqui: `close()` e desmontagem/troca de token, nao
      // queda de rede. Reportar offline no cleanup do efeito pintava a tela de
      // "sem conexao" a cada refresh de token — e, se o efeito seguinte saisse
      // cedo por falta de token, o estado ficava cravado em offline sem volta.
      socket?.close();
      socket = null;
    },
  };
}
